import React, { useState, useEffect } from 'react';

import Breakpoints from 'lib/Breakpoints';
import ChildrenPropType from 'lib/CustomPropTypes/children';
import scrollWindowTo from 'lib/scrollToElement';

import InternalPanelList from './InternalPanelList';
import InternalPanelListItem from './InternalPanelListItem';
import InternalTabList from './InternalTabList';
import InternalTabListItem from './InternalTabListItem';

const renderInternalPanelItem = (panel, idx, activePanelIndex) => {
  const { props: { children: panelChildren } } = panel;

  return (
    <InternalPanelListItem key={idx} show={idx === activePanelIndex}>
      { panelChildren }
    </InternalPanelListItem>
  );
};

const renderInternalTabListItem = (panel, idx, activePanelIndex, setActivePanel) => {
  const { props: { tabText } } = panel;
  const handleClick = () => setActivePanel(idx);

  return (
    <InternalTabListItem
      key={idx}
      active={idx === activePanelIndex}
      onClick={handleClick}
      text={tabText}
    />
  );
};

export const TabbedContent = ({ children }) => {
  const [isSmallWindow, setIsSmallWindow] = useState(null);
  const [activePanel, setActivePanel] = useState(0);

  /**
   * State change effect to trigger scrolling to the top of the tabbed content
   */
  useEffect(() => {
    const currentYPositionOfWindow = window.scrollY;
    const yPositionOfStickyTabs = Breakpoints.isS()
      ? 50
      : 197;

    if (currentYPositionOfWindow > yPositionOfStickyTabs) {
      scrollWindowTo(yPositionOfStickyTabs);
    }
  }, [activePanel]);

  /**
   * Mount/unmount effect subscribe to window size changes
   */
  useEffect(() => {
    function checkWindowSize() {
      setIsSmallWindow(Breakpoints.isSorM());
    }

    checkWindowSize();

    window.addEventListener('resize', checkWindowSize);

    return () => {
      window.removeEventListener('resize', checkWindowSize);
    };
  }, []);

  // Tabbed content is a mobile/tablet-only feature. Rendering on other sizes is unnecessary and can
  // cause side-effect bugs.
  if (!isSmallWindow) {
    return null;
  }

  const renderedInternalTabListItems = children
    .map((panel, idx) => renderInternalTabListItem(panel, idx, activePanel, setActivePanel));
  const renderedInternalPanelItems = children
    .map((panel, idx) => renderInternalPanelItem(panel, idx, activePanel));

  return (
    <div className="tabbed-content">
      <InternalTabList>
        {renderedInternalTabListItems}
      </InternalTabList>

      <InternalPanelList>
        {renderedInternalPanelItems}
      </InternalPanelList>
    </div>
  );
};

TabbedContent.propTypes = {
  children: ChildrenPropType.isRequired,
};
